/**
 * MarginExplorer — the INVOICE fact's two views, behind tabs. TREND is margin by invoice
 * month with the cost-mix ring beside it; BREAKDOWN is the reconciling drill. Same shape as
 * the order-book explorer, and deliberately so: only the fact underneath changes.
 *
 * WHAT CHANGED UNDERNEATH, and why the surface is simpler for it. /margin used to read the
 * DEMAND fact filtered to its delivered half, which meant inferring delivery from quantities,
 * pricing at LIST rather than at what was billed, costing from a part average with no period
 * in it, and applying no FX rate. It now reads main.fct_margin_line: billed value in home
 * currency, costed by the job that made the line. So there is no `half` control here — every
 * row is billed work — and the window is the INVOICE month, the basis that ties to Sage.
 *
 * The drill's descent chain comes from the server: entity → <axis> → invoice → line at Group
 * scope, <axis> → invoice → line when the top bar has already picked an entity.
 *
 * Each level renders the cost/margin bar with the GM% line and the sortable table — ALL from
 * the one served payload, so descending updates them together and they reconcile by
 * construction (constitution §3).
 *
 * THE HEADER CARDS SIT ABOVE THE TABS (20 Aug 2026), not inside the breakdown. They state
 * what you are looking at now — the scoped window on the trend, the current node on the
 * breakdown — so the surface reports its totals once. The page's old hero tiles went with
 * them: those read a single Sage month while the scope bar named a year.
 *
 * ⭐ AND THERE IS NO COST-BASIS CONTROL EITHER (24 Aug 2026). There used to be one, carried
 * from useOrderBookNav down to the controls row, the cards, the chart and the trend. It never
 * had anything to choose: `cost_basis` is a property of the row and does not vary inside a
 * site — Kirkstall is job_actual on 10,828 of 10,828 rows, Oracle quote_estimated on 1,414 of
 * 1,414 — so it did nothing at either site and, at Group, showed one entity's cost while
 * hiding the other's. The server serves one cost; MarginTotals states what it is made of.
 *
 * ⚠ THE SERVED WINDOW IS NOT ALWAYS THE ASKED-FOR WINDOW (23 Aug 2026). The server serves
 * CLOSED months only, so a scope of 2026 asked on 23 Aug gets January to July. `span` states
 * what was asked and `open_months_excluded` states what was removed; both go to MarginTotals,
 * because the cards are where the difference shows up. Do not reconcile the gap by widening
 * this surface into unbilled work — that is the order book, it has its own menu item, and it
 * is not built for Kaleidex yet.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from './InfoBanner'
import DrillTable from './kit/DrillTable'
import SumCheckBanner from './kit/SumCheckBanner'
import MarginTotals from './MarginTotals'
import OrderBookControls from './OrderBookControls'
import OrderBookTabs from './OrderBookTabs'
import MarginTrend from './MarginTrend'
import BreakdownChartCard from './kit/BreakdownChartCard'
import { useMargin, useMarginTimeline } from '../hooks/useMargin'
import { useOrderBookNav } from '../hooks/useOrderBookNav'
import { COST_COMPONENTS, ONE_COST, defaultSortFor } from '../viewmodels/orderBook'
import { MARGIN_COPY, monthOrNull, servedSpan } from '../viewmodels/marginFact'
import { ERP_OF } from './MarginBridge'
import type { DrillRow, PathSegment, Site } from '../types/metrics'
import { compare, EMPTY_SAGE, type SageWindow } from '../viewmodels/marginSage'
import { tiles } from '../viewmodels/marginCoverage'
import type { SageSubs } from './OrderBookTiles'
import type { MarginBreakdown, MarginTotals as MarginTotalsShape } from '../types/margin'

/** The axis labels this fact uses. Month means the INVOICE month here, never the demand
 * date — the distinction is the whole point of the fact, so the control has to say it. */
const AXIS_LABEL = { customer: 'Customer', part: 'Part', month: 'Invoice month' } as const

/** The composition columns belong to the chart, not the table — they would triple its
 * width for a breakdown the bars already show. They stay in the row (and so in the CSV). */
const CHART_ONLY = new Set<string>(COST_COMPONENTS)

/** One served level — chart · table off the SAME payload; bars descend like rows. The node's
 * cards are above the tabs, on the same `totals` this chart reads. */
function LevelView({ data, onDescend }: {
  data: MarginBreakdown
  onDescend: (seg: PathSegment) => void
}): React.JSX.Element {
  const descentFor = (row: DrillRow): PathSegment | null =>
    data.descend_key ? { level: data.level, id: String(row[data.descend_key]) } : null
  const onBarClick = data.descend_key
    ? (row: DrillRow) => {
        const seg = descentFor(row)
        if (seg) onDescend(seg)
      }
    : undefined
  const suffix = data.path.length ? '_' + data.path.map((s) => s.id).join('_') : ''
  return (
    <>
      <BreakdownChartCard data={data} columns={ONE_COST} measure="revenue" onBarClick={onBarClick} />
      <SumCheckBanner sumCheck={data.sum_check} />
      <DrillTable
        rows={data.rows}
        exportName={`margin_${data.level}${suffix}`}
        descentFor={descentFor}
        onDescend={onDescend}
        hideColumns={CHART_ONLY}
        defaultSort={defaultSortFor(data.level, data.descend_key)}
        ranked
      />
    </>
  )
}

/** The drill's own state: served, loading, refused or genuinely empty — each said plainly. */
function DrillBody({ query, site, scopeNote, onDescend }: {
  query: ReturnType<typeof useMargin>
  site: Site
  scopeNote: string
  onDescend: (seg: PathSegment) => void
}): React.JSX.Element {
  if (query.error) {
    return <InfoBanner variant="error" icon={AlertCircle} title="Margin analysis unavailable" message={query.error.message} />
  }
  if (!query.data) {
    return <div role="status" className="animate-pulse rounded-xl border border-line px-4 py-6 text-center text-xs text-mut2">Loading…</div>
  }
  if (query.data.rows.length === 0) {
    return <InfoBanner variant="info" message={`${MARGIN_COPY.empty} ${site}${scopeNote}.`} />
  }
  return <LevelView data={query.data} onDescend={onDescend} />
}

/** Only a real monthly [from, to] scopes the fact (the backend windows on the invoice date),
 * so a malformed one is no window at all rather than a claim we did not apply. The trend keeps
 * the WHOLE window; a held month narrows only the drill beneath it. */
function windowsFor(periodFrom: string | null, periodTo: string | null, month: string | null) {
  const [lo, hi] = [monthOrNull(periodFrom), monthOrNull(periodTo)]
  const [from, to] = lo !== null && hi !== null ? [lo, hi] : [null, null]
  const [drillFrom, drillTo] = month ? [month, month] : [from, to]
  // A span is READ, not parsed, so it carries the platform's month label — 'Jan 26 → Dec 26'.
  // `windowSpan` is the whole window, which is what the trend plots whatever month is held.
  const span = servedSpan(drillFrom, drillTo)
  return { from, to, drillFrom, drillTo, span, note: span ? ` invoiced ${span}` : '' }
}

/** The ledger figure for each card, or an empty set when there is no ledger to compare to.
 * `sageComparable` is the page's confirmation that both cover the same span. */
function sageFor(
  totals: MarginTotalsShape | undefined, sage: SageWindow | undefined, comparable: boolean
): SageSubs {
  return !totals || !sage ? {} : compare(tiles(totals), sage, comparable)
}

/** The breakdown tab: controls · chart · ranked table, all off one payload. */
function BreakdownTab({ nav, site, windows, query }: {
  nav: ReturnType<typeof useOrderBookNav>
  site: Site
  windows: ReturnType<typeof windowsFor>
  query: ReturnType<typeof useMargin>
}): React.JSX.Element {
  return (
    <>
      <OrderBookControls
        path={nav.path}
        rootLabel={site === 'Group' ? 'Group' : site}
        axis={nav.axis}
        axisLabel={AXIS_LABEL}
        level={query.data?.level ?? ''}
        month={nav.month}
        onJump={(depth) => nav.setPath(nav.path.slice(0, depth))}
        onAxis={nav.setAxis}
        onClearMonth={() => nav.setMonth(null)}
      />
      <DrillBody query={query} site={site} scopeNote={windows.note} onDescend={nav.descend} />
    </>
  )
}

export default function MarginExplorer({ site, periodFrom = null, periodTo = null, sage, sageComparable = false }: {
  site: Site
  periodFrom?: string | null
  periodTo?: string | null
  /** The ledger's four figures for this site and window (viewmodels/marginSage). */
  sage?: SageWindow
  /** Does the ledger cover the SAME span as the cards? Only the page knows both. */
  sageComparable?: boolean
}): React.JSX.Element {
  const nav = useOrderBookNav()
  const w = windowsFor(periodFrom, periodTo, nav.month)
  const onTrend = nav.tab === 'trend'
  const timeline = useMarginTimeline(w.from, w.to, site, onTrend)
  const breakdown = useMargin(nav.path, w.drillFrom, w.drillTo, site, nav.axis, !onTrend)
  // The REQUEST carries the asked-for window; the LABEL carries what came back. The server
  // drops unclosed months, so those two differ — `servedSpan` CLAMPS the span to what was
  // actually served, and that span is now the only place the exclusion is stated. It is
  // enough: a card headed "invoiced Jan 26 → Jul 26" has said August is not in it.
  const openMonths = (onTrend ? timeline.data : breakdown.data)?.open_months_excluded ?? []
  return (
    <div>
      <MarginTotals
        totals={(onTrend ? timeline.data?.total : breakdown.data?.totals) ?? null}
        span={onTrend ? servedSpan(w.from, w.to, openMonths) : servedSpan(w.drillFrom, w.drillTo, openMonths)}
        erp={ERP_OF[site] ?? 'the ERP'}
        site={site}
        sage={sageFor(onTrend ? timeline.data?.total : breakdown.data?.totals, sage, sageComparable)}
        ledger={sage ?? EMPTY_SAGE}
      />
      <OrderBookTabs tab={nav.tab} onSelect={nav.setTab} trendSubtitle="margin over the invoice calendar" />
      <div className="space-y-2.5 px-4 py-3.5">
        {onTrend ? (
          <MarginTrend query={timeline} site={site} selected={nav.month} onSelect={nav.setMonth} />
        ) : (
          <BreakdownTab nav={nav} site={site} windows={w} query={breakdown} />
        )}
      </div>
    </div>
  )
}
